import React, { useCallback } from 'react'

import {
  ChevronRightIcon,
  LocationIcon,
  SearchIcon
} from '@toasttab/buffet-pui-icons'

import { RangedCustomerAddress } from 'src/public/components/online_ordering/DeliveryContext'
import { CartDeliveryInfo } from 'src/public/components/online_ordering/FulfillmentContext'
import { ModalCloseButton } from 'src/shared/components/common/modal/Modal'
import { formatDeliveryAddress } from 'src/public/components/online_ordering/address_configs'

import Button from 'src/shared/components/common/button'
import { t } from 'src/translations'

import { ScreenProps } from './DeliveryModal'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useTracker } from 'src/lib/js/hooks/useTracker'
import {
  getInvalidTrackingSource,
  trackEnteredDeliveryAddress,
  trackInvalidDeliveryAddressEntered,
  trackValidDeliveryAddressEntered
} from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { useFlags } from 'launchdarkly-react-client-sdk'

/**
 * The primary screen used to present the following:
 * The active delivery address set in the FulfillmentContext (such that it applies to sessions with and without active Carts)
 * The ability to search for an address to update their ordering session to use.
 * The ability to select from saved addresses to update their ordering session to use.
 */
const AddressHubScreen = ({
  goToScreen,
  closeModal,
  deliveryAddress,
  inRangeAddresses,
  outOfRangeAddresses,
  trackingSource
}: ScreenProps) => {
  const tracker = useTracker()
  const { cart } = useCart()
  const { customer } = useCustomer()
  const { ooRestaurant } = useRestaurant()
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  const hasSaveAddresses =
    inRangeAddresses.length > 0 || outOfRangeAddresses.length > 0

  const handleValidAddressSelection = useCallback(
    (address: RangedCustomerAddress) => {
      const { name, guid, deliveryInfo } = address
      const addressPayload = {
        name,
        guid,
        ...deliveryInfo
      }
      goToScreen('address-confirm', addressPayload)
      trackEnteredDeliveryAddress(
        tracker,
        cart?.order?.selections?.length || 0,
        true,
        trackingSource
      )
      trackValidDeliveryAddressEntered(
        tracker,
        cart?.order?.selections?.length || 0,
        true,
        trackingSource
      )
    },
    [cart?.order?.selections?.length, goToScreen, tracker, trackingSource]
  )

  const handleInvalidAddressSelection = useCallback(
    (address: RangedCustomerAddress) => {
      trackEnteredDeliveryAddress(
        tracker,
        cart?.order?.selections?.length || 0,
        true,
        trackingSource
      )
      trackInvalidDeliveryAddressEntered(
        tracker,
        cart?.order?.selections?.length || 0,
        true,
        trackingSource
      )
      goToScreen(
        'address-invalid',
        address.deliveryInfo,
        getInvalidTrackingSource(trackingSource)
      )
    },
    [cart?.order?.selections?.length, goToScreen, tracker, trackingSource]
  )

  const handleCurrentAddressSelect = useCallback(() => {
    if (deliveryAddress) {
      goToScreen('address-confirm', deliveryAddress)
    }
  }, [deliveryAddress, goToScreen])

  const handleSearchForAddress = useCallback(() => {
    goToScreen('address-search')
  }, [goToScreen])

  return (
    <div data-testid='address-hub-screen' className='addressHubScreen'>
      <div className='modalHeader'>
        <h2 id='delivery-address-modal-title' tabIndex={-1}>
          {t('address-hub-screen.title')}
        </h2>
        <ModalCloseButton onClose={closeModal} />
      </div>
      <div className='modalContent'>
        <div className='searchAddressContainer'>
          <FakeSearchInput onSearchClick={handleSearchForAddress} />
        </div>
        <div className='hubActions'>
          <div className='orderSummary'>
            {deliveryAddress ? (
              <>
                <h3 className='hubSubHeading'>
                  {t('address-hub-screen.address-for-order')}
                </h3>
                <AddressButton
                  key={deliveryAddress.guid}
                  address={deliveryAddress}
                  formattedAddress={formatDeliveryAddress(
                    deliveryAddress,
                    {
                      full: true,
                      includeAddress2: true,
                      includeZip: false,
                      country: ooRestaurant?.i18n.country
                    },
                    intlAddressCountryLogicErrorHandling
                  )}
                  onSelect={handleCurrentAddressSelect}
                />
              </>
            ) : (
              <div
                className='selectCallout'
                data-testid='address-hub-no-address'
              >
                <span>{t('address-hub-screen.select-address-or-search')}</span>
              </div>
            )}
          </div>
          {customer && hasSaveAddresses && (
            <div
              className='savedAddresses'
              data-testid='address-hub-saved-addresses'
            >
              <h3 className='hubSubHeading'>
                {t('address-hub-screen.saved-addresses')}
              </h3>
              <div className='savedAddressButtons'>
                {inRangeAddresses.map((rangedAddress) => {
                  return (
                    <AddressButton
                      key={rangedAddress.guid}
                      address={rangedAddress}
                      formattedAddress={formatDeliveryAddress(
                        rangedAddress.deliveryInfo,
                        {
                          full: true,
                          includeAddress2: true,
                          includeZip: false,
                          country: ooRestaurant?.i18n.country
                        },
                        intlAddressCountryLogicErrorHandling
                      )}
                      onSelect={() =>
                        handleValidAddressSelection(rangedAddress)
                      }
                    />
                  )
                })}
                {/* Show invalid addresses at the end, still selectable */}
                {outOfRangeAddresses.map((rangedAddress) => {
                  return (
                    <AddressButton
                      key={rangedAddress.guid}
                      address={rangedAddress}
                      formattedAddress={formatDeliveryAddress(
                        rangedAddress.deliveryInfo,
                        {
                          full: true,
                          includeAddress2: true,
                          includeZip: false,
                          country: ooRestaurant?.i18n.country
                        },
                        intlAddressCountryLogicErrorHandling
                      )}
                      onSelect={() => {
                        handleInvalidAddressSelection(rangedAddress)
                      }}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const AddressButton = ({
  address,
  formattedAddress,
  onSelect
}: {
  address: RangedCustomerAddress | CartDeliveryInfo
  formattedAddress: string
  onSelect: () => void
}) => {
  const addressName = address.name && address.name.trim()
  const addressHeading = addressName ? address.name : formattedAddress

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect()
    }
  }

  return (
    <Button
      as='button'
      className='addressOption'
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      ariaLabel={t('address-hub-screen.select-address-aria', {
        address: formattedAddress
      })}
      testId={`address-option-${address.guid}`}
    >
      <div className='addressContent'>
        <LocationIcon accessibility='decorative' />
        <div className='address'>
          <div className='addressHeading'>{addressHeading}</div>
          {addressName && (
            <div className='addressSubHeading'>{formattedAddress}</div>
          )}
        </div>
      </div>
      <ChevronRightIcon />
    </Button>
  )
}

const FakeSearchInput = ({ onSearchClick }: { onSearchClick: () => void }) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSearchClick()
    }
  }

  return (
    <div className='fakeSearchContainer'>
      <button
        className='fakeSearchInput'
        onClick={onSearchClick}
        onKeyDown={handleKeyDown}
        aria-label={t('address-hub-screen.search-for-delivery-address')}
        type='button'
        data-testid='address-hub-search-button'
      >
        <SearchIcon accessibility='decorative' className='inputSearchIcon' />
        <span className='placeholder'>
          {t('fulfillment-general.enter-delivery-address')}
        </span>
      </button>
    </div>
  )
}

export default AddressHubScreen
